import { Save } from "@mui/icons-material";
import {
  Button,
  CircularProgress,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import useThreeJsLoader from "../../zustand/stores/useThreeJsLoader";
import ConfigDataBank from "../configurator/configuratorForms/ConfigDataBank";

const PREFIX = "GetModelOrImageUrl";

const classes = {
  fetchingContainer: `${PREFIX}-fetchingContainer`,
  loadingIcon: `${PREFIX}-loadingIcon`,
  createAvatarButton: `${PREFIX}-createAvatarButton`,
  importFileFromDiskContainer: `${PREFIX}-importFileFromDiskContainer`,
  buttonFontSize: `${PREFIX}-buttonFontSize`,
  uploadGltfUrlContainer: `${PREFIX}-uploadGltfUrlContainer`,
  saveUrlValueButton: `${PREFIX}-saveUrlValueButton`,
};

const GetModelOrImageUrlContainer = styled("div")(({ theme }) => {
  return {
    [`& .${classes.fetchingContainer}`]: {
      textAlign: "center",
    },
    [`& .${classes.loadingIcon}`]: {
      color: theme.palette.primary.main,
    },
    [`& .${classes.createAvatarButton}`]: {
      margin: "1rem auto",
      width: "200px",
      fontSize: "0.875rem",
    },
    [`& .${classes.importFileFromDiskContainer}`]: {
      margin: "1rem 0",
    },
    [`& .${classes.buttonFontSize}`]: {
      fontSize: "0.8rem",
    },
    [`& .${classes.uploadGltfUrlContainer}`]: {
      display: "flex",
      flexDirection: "row",
      margin: "1rem 0",
      ".Mui-disabled": {
        color: "rgba(255, 255, 255, 0.3)",
        boxShadow: "none",
        backgroundColor: "rgba(255, 255, 255, 0.12)",
      },
    },
    [`& .${classes.saveUrlValueButton}`]: {
      marginLeft: "1rem",
      maxHeight: "56px",
    },
  };
});

const GetModelOrImageUrl = ({
  loaders,
  setActionStatus,
  errors,
  setErrors,
  setFields,
  internalState,
  setInternalState,
  getS3UrlFromApi,
  modelUrl,
}) => {
  const { t } = useTranslation();
  const setReinitialize = useThreeJsLoader((state) => state.setReinitialize);

  if (loaders.lockAPI) {
    return (
      <GetModelOrImageUrlContainer>
        <div className={classes.fetchingContainer}>
          <CircularProgress
            size={28}
            color="inherit"
            className={classes.loadingIcon}
          />
          <Typography variant="body1">{t("loaders.fetching")}</Typography>
        </div>
      </GetModelOrImageUrlContainer>
    );
  } else {
    return (
      <GetModelOrImageUrlContainer>
        <Button
          color="primary"
          variant="contained"
          className={classes.createAvatarButton}
          onClick={() =>
            setActionStatus((draft) => {
              draft.isAvatarOpen = true;
            })
          }
        >
          {t("myExpariences.form.createAvatarButton")}
        </Button>
        <Typography variant="h6">
          {t("myExpariences.form.orModelSubtitle")}
        </Typography>
        <div className={classes.importFileFromDiskContainer}>
          <input
            type="file"
            accept=".gltf,.glb"
            name={`import-3d-model`}
            id={`import-3d-model`}
            style={{ display: "none" }}
            onClick={(e) => (e.target.value = null)} //reinitialize the value to retrigger the onChange on the same file
            onChange={(e) => {
              getS3UrlFromApi(e.target.files);
            }}
          />
          <label htmlFor={`import-3d-model`}>
            <Button
              color="primary"
              aria-label="add 3d model"
              component="span"
              variant="contained"
              className={classes.buttonFontSize}
            >
              {t("myExpariences.form.importFileButton")}
            </Button>
          </label>
        </div>
        <Typography variant="h6">
          {t("myExpariences.form.orModelSubtitle")}
        </Typography>
        <div className={classes.importFileFromDiskContainer}>
          <input
            type="file"
            accept=".jpg,.jpeg,.png,.psd,.tiff,.tga,.gif,.bmp,.iff,.pict"
            name={`import-image`}
            id={`import-image`}
            style={{ display: "none" }}
            onClick={(e) => (e.target.value = null)} //reinitialize the value to retrigger the onChange on the same file
            onChange={(e) => {
              getS3UrlFromApi(e.target.files, true);
            }}
          />
          <label htmlFor={`import-image`}>
            <Button
              color="primary"
              aria-label="add image model"
              component="span"
              variant="contained"
              className={classes.buttonFontSize}
            >
              {t("myExpariences.form.importImageButton")}
            </Button>
          </label>
        </div>
        <ConfigDataBank
          mode="customs"
          onSelectMedia={(media) =>
            setInternalState((draft) => {
              draft.uploadModelFromUrl = media;
            })
          }
        />
        <div className={classes.uploadGltfUrlContainer}>
          <TextField
            name={`3d-model-url`}
            label={t("myExpariences.form.modelUrlPlaceholder")}
            value={internalState.uploadModelFromUrl}
            onChange={(e) => {
              setInternalState((draft) => {
                draft.uploadModelFromUrl = e.target.value;
              });
            }}
            error={
              errors.isUrlInvalid ||
              modelUrl === internalState.uploadModelFromUrl
            }
            helperText={
              errors.isUrlInvalid
                ? t("myExpariences.form.incorrectUrl")
                : modelUrl === internalState.uploadModelFromUrl
                ? t("myExpariences.form.modelCurrentlyInUse")
                : ""
            }
            fullWidth
            variant={"outlined"}
          />
          <Button
            variant="contained"
            color="primary"
            disabled={
              internalState.uploadModelFromUrl.length === 0 ||
              modelUrl === internalState.uploadModelFromUrl
            }
            onClick={() => {
              if (
                /^(ftp|http|https):\/\/[^ "]+$/.test(
                  internalState.uploadModelFromUrl
                ) &&
                (internalState.uploadModelFromUrl.includes(".glb") ||
                  internalState.uploadModelFromUrl.includes(".gltf") ||
                  internalState.uploadModelFromUrl.includes(".jpeg") ||
                  internalState.uploadModelFromUrl.includes(".jpg") ||
                  internalState.uploadModelFromUrl.includes(".png") ||
                  internalState.uploadModelFromUrl.includes(".webp"))
              ) {
                // setRegeneratePreview(true);
                setReinitialize();
                setErrors((draft) => {
                  draft.isUrlInvalid = false;
                });
                setFields((draft) => {
                  draft.grid.modelUrl = internalState.uploadModelFromUrl;
                });
                setInternalState((draft) => {
                  draft.isAccordionOpen = false;
                });
              } else {
                setErrors((draft) => {
                  draft.isUrlInvalid = true;
                });
              }
            }}
            className={classes.saveUrlValueButton}
          >
            <Save />
          </Button>
        </div>
      </GetModelOrImageUrlContainer>
    );
  }
};

export default GetModelOrImageUrl;
